import { logger } from '@magicschool/logger';
import { getHost, getLoginUrl, getStudentUrl, getTeacherUrl } from '@magicschool/utils/nextjs/url';

const RELATIVE_URL_BASE = 'https://sanitize.invalid';

/**
 * Sanitizes a URL to ensure it is safe and within allowed domains.
 * if we didn't have this function, we could be vulnerable to XSS attacks
 * and things like open redirects, which could lead to phishing attacks.
 */
export function sanitizeUrl(url: string | null | undefined, defaultUrl = '/'): string {
  const allowedDomains = [getTeacherUrl(), getLoginUrl(), getStudentUrl(), getHost(process.env.NEXT_PUBLIC_SUPABASE_URL)] as const;

  // If url is null or empty, return the default URL just for a cleaner fallback experience
  if (!url || url.trim() === '') return defaultUrl;

  // Check for potentially unsafe URL schemes that could lead to XSS attacks
  const unsafeSchemes = ['javascript:', 'data:', 'vbscript:', 'blob:', 'file:'];
  if (unsafeSchemes.some((scheme) => url.toLowerCase().startsWith(scheme))) {
    logger.error('Potentially unsafe URL scheme detected', { url });
    return defaultUrl;
  }

  // Block protocol-relative URLs (starting with //)
  if (url.startsWith('//')) {
    logger.error('Potentially unsafe URL scheme detected', { url });
    return defaultUrl;
  }

  // A relative URL is safe only if it stays relative once parsed. Browsers treat `\` as `/`
  // and strip tabs and newlines, so `/\evil.com` and `/\t/evil.com` both resolve to another
  // host even though they start with a single `/`. Some of these (`/\ex ample.com`) fail to
  // parse at all, and those fall back to the default too.
  if (url.startsWith('/') || url.startsWith('./')) {
    try {
      if (new URL(url, RELATIVE_URL_BASE).origin === RELATIVE_URL_BASE) return url;
    } catch {
      logger.error('Relative URL failed to parse', { url });
      return defaultUrl;
    }

    logger.error('Relative URL resolves to another origin', { url });
    return defaultUrl;
  }

  try {
    // otherwise just ensure the hostname is a domain that we control
    const hostname = getHost(url);
    const isAllowedDomain = allowedDomains.some((domain) => hostname === domain);
    return isAllowedDomain ? url : defaultUrl;
  } catch {
    // If URL parsing fails, let's assume it is malformed
    return defaultUrl;
  }
}
